import React, { createContext, useEffect, useState, useCallback } from 'react'
import { validateClient, validateClientAddress } from '../validators/client'
import {
    saveClient,
    saveClientAddress,
    getClientAddresses,
    updateClientAddress
} from '../firebase/obj/client'
import { calculateTotalOrder } from '../utils/order'
import {
    deleteOrder,
    saveOrder,
    updateOrder,
    getOrdersByClientIdRealTime,
    lastOrderByClient
} from '../firebase/obj/order'
import useMainContext from '../hooks/useMainContext'
import useZoneContext from '../hooks/useZoneContext'
import moment from 'moment'
import { isPointInsideZone } from '../utils'

export const OrderContext = createContext()

const Provider = ({ children }) => {
    const [order, setOrder] = useState({})
    const [error, setError] = useState()
    const { user, config, setCart, products } = useMainContext()
    const { zones } = useZoneContext()
    const [myActiveOrders, setMyActiveOrders] = useState([])
    const [myLastOrders, setMyLastOrders] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    // Función para cargar órdenes del usuario logueado
    const loadOrders = useCallback(async () => {
        if (!user) return
        if (user.admin) return

        setIsLoading(true)
        setError(null)

        try {
            // Cargar últimas órdenes
            const fetchedLastOrders = await lastOrderByClient(
                user.uid,
                ['Entregado', 'Cancelado', 'Pago pendiente'],
                10
            )
            setMyLastOrders(fetchedLastOrders || [])
        } catch (err) {
            setError(err)
            setMyLastOrders([])
        } finally {
            setIsLoading(false)
        }
    }, [user])

    // Listener de órdenes en tiempo real
    useEffect(() => {
        if (!user) return
        if (user.admin) return

        const activeOrderStates = ['Pendiente', 'En cocina', 'Demorado', 'Finalizado', 'En reparto']
        const unsubscribe = getOrdersByClientIdRealTime({
            clientId: user.uid,
            orderStates: activeOrderStates,
            setState: setMyActiveOrders,
            onError: (err) => {
                setError(err)
                setMyActiveOrders([])
            }
        })

        // Cargar órdenes iniciales
        loadOrders()

        // Limpiar suscripción
        return () => {
            if (unsubscribe) unsubscribe()
        }
    }, [user, loadOrders])

    useEffect(() => {
        if (config && !order?.id) {
            if (!order?.deliveryPrice) {
                const _order = { ...order, deliveryPrice: Number(config?.deliveryPrice || 0) }
                const { total, pickupDiscount, totalSavings } = calculateTotalOrder({ ..._order, config })
                _order.amount = total
                _order.pickupDiscount = pickupDiscount
                _order.totalSavings = totalSavings
                setOrder(_order)
            }
        }
    }, [config])

    // useEffect(() => {
    //     console.log('order', order)
    // }, [order])

    const handleSendOrder = () => {
        // console.log('handleSendOrder', order)
        return saveOrder(order)
    }


    const handleSaveClient = (client) => new Promise((resolve, reject) => {
        try {
            let newClient = false

            const clientValidation = validateClient(client)

            if (!clientValidation.valid)
                return clientValidation


            if (!client.id) {
                newClient = true
                client.active = true
            }
            saveClient(client)
                .then(result => {
                    if (newClient) {
                        setOrder((lastState) => {
                            return { ...lastState, clientId: result.id }
                        })
                    }
                    resolve({ message: 'Cliente guardado', id: result.id })
                })
                .catch(reject)


        } catch (error) {
            console.error('handleSaveClientData', error)
            setError(error?.message)
        }
    })

    const handleSaveClientAddress = async (address) => {
        try {
            const addressValidation = validateClientAddress(address)

            if (!addressValidation.valid)
                throw new Error(addressValidation.message || 'La dirección no es válida.')

            // console.log('handleSaveClientAddress', address)
            if (!address.id) {
                address.active = true
                address.principal = true
                const { id } = await saveClientAddress(address)

                address.id = id
                setOrder((lastState) => {
                    return { ...lastState, address }
                })
                return { message: 'Dirección guardada', address }

            } else {
                await updateClientAddress(address)
                return { message: 'Dirección actualizada', address }
            }

        } catch (error) {
            console.error('handleSaveClientAddress', error)
            setError(error?.message)
            throw error
        }
    }

    const handleSetOrder = (o) => {
        try {
            // console.log('handleSetOrder', order)
            // console.log('o', o)
            if (o?.type === 'table' || o?.pickup) {
                // console.log('table or pickup')
                o.deliveryPrice = 0
            } else if (!o.deliveryPrice && !order.deliveryPrice) {
                // console.log('no delivery price')
                o.deliveryPrice = Number(config?.deliveryPrice || 0)
            }

            // console.log('o.position', o.position)
            if (o.position && o.position.length && (o.position !== order.position)) {

                const zone = isPointInsideZone(o.position, zones)
                console.log('zone', zone)
                if (zone) {
                    o.deliveryPrice = Number(zone.price)
                    o.zoneName = zone.name
                    o.zoneId = zone.id
                } else {
                    o.deliveryPrice = Number(config?.deliveryPrice || 0)
                    o.zoneName = ''
                    o.zoneId = ''
                }
            }
            const { total, pickupDiscount, totalSavings } = calculateTotalOrder({ ...o, config })

            o.amount = total
            o.pickupDiscount = pickupDiscount
            o.totalSavings = totalSavings
            if (!o.id) {
                if (o.createdByClient) {
                    o.orderState = 'Pendiente'
                    if (o.paymentMethod === 'polarsh') {
                        o.orderState = 'Pago pendiente'
                    }
                } else {
                    o.orderState = 'En cocina'
                }
                // console.log('handleSetOrder', order.date)
                if (!o.date || moment(o.date).isBefore(moment())) {
                    o.date = new Date()
                }
            }
            console.log('o', o)
            setOrder(o)
            return o
        } catch (error) {
            console.error('handleSetOrder', error)
        }

    }

    const handleUpdateOrder = (order) => updateOrder(order)
    const handleDeleteOrder = (id) => deleteOrder(id)

    const clearCurrentOrder = () => setOrder({ deliveryPrice: config?.deliveryPrice || 0, position: config?.position || [] })

    const repeatOrder = (order) => {
        try {
            // console.log('productsList', order.productsList)

            const productsList = []

            order.productsList.forEach(p => {
                //debo comparar si los productos siguen activos, si su composición sigue siendo la misma y su precio
                const product = products.find(pr => pr.id === p.id)
                if (!product?.active || !product?.stock) return

                product.qty = p.qty
                product.notes = p.notes || ''
                product.smartNotes = p.smartNotes || ''


                product.smartComposition = []

                p.smartComposition?.forEach(sc => {
                    const newSc = p.smartComposition?.find(ol => ol.id === sc.id)
                    if (!newSc) return sc

                    newSc.qty = sc.qty
                    newSc.notes = sc.notes || ''

                    // console.log(sc.name, sc.composition)

                    newSc.composition = sc.composition?.map(c => {
                        const newC = newSc.composition?.find(ol => ol.id === c.id)
                        if (!newC) return c

                        newC.qty = c.qty || 1
                        newC.notes = c.notes || ''
                        newC.checked = c.checked || false

                        return c
                    })

                    product.smartComposition.push(newSc)
                })

                productsList.push(product)
            })

            const newOrder = {
                createdByClient: true,
                date: new Date(),
                clientName: order.clientName || '',
                clientId: order.clientId || '',
                clientPhone: order.clientPhone || '',
                userEmail: order.userEmail || '',
                paymentMethod: order.paymentMethod || '',
                address: order.address || '',
                addressId: order.addressId || '',
                position: order.position || [],
                orderState: order.paymentMethod === 'polarsh' ? 'Pago pendiente' : 'Pendiente',
                pickup: order.pickup || false,
                productsList,
                orderRepeatedId: order.id || ''
            }

            const o = handleSetOrder(newOrder)
            setCart(o)
        } catch (error) {
            console.error('repeatOrder', error)
        }
    }

    const value = {
        order,
        myActiveOrders,
        myLastOrders,
        isLoading,
        setOrder: handleSetOrder,
        sendOrder: handleSendOrder,
        updateOrder: handleUpdateOrder,
        deleteOrder: handleDeleteOrder,
        clearCurrentOrder,
        getClientAddresses,
        saveClient: handleSaveClient,
        saveClientAddress: handleSaveClientAddress,
        repeatOrder
    }

    return (
        <OrderContext.Provider value={value}>
            {children}
        </OrderContext.Provider>
    )
}

const context = {
    Provider,
    Consumer: OrderContext.Consumer
}

export default context